import { UnixTime } from 'types/measurement/time'
import { RolodexEntryJson } from 'types/rolodex/rolodexEntries'
import { parseNumber, parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'

export interface InlineFeedback {
  rating: 'up' | 'down' | null
  comment?: string
  timestamp: UnixTime
}

/**
 * User action types that can be tracked during a chat session
 */
export type UserActionType =
  | 'stop_generation'
  | 'edit_message'
  | 'resend_message'

export interface UserAction {
  type: UserActionType
  timestamp: UnixTime
  messageIndex?: number
  originalText?: string
  newText?: string
}

export interface SerializedThinkingNote {
  text: string
  sources?: Array<{ id: string; text: string; data: Record<string, unknown> }>
  feedback?: InlineFeedback
}

/**
 * Serialized conversation message for storage.
 * Feedback is stored inline on each message rather than in a separate array.
 */
export interface SerializedConversationMessage {
  type: 'thought' | 'error' | 'aiResponse' | 'userMessage'
  text?: string
  naturalLanguageResponse?: string
  sources?: Array<{
    id: string
    text: string
    data: Record<string, unknown>
  }>
  thinkingTimeSeconds?: number
  thinkingNotes?: SerializedThinkingNote[]
  instrumentsWithData?: Array<{
    instrumentName: string
    entryId: string
    startDatetime?: string
    endDatetime?: string
  }>
  feedback?: InlineFeedback
}

export interface AgentContextData {
  [key: string]: unknown
  sessionId: string
  userId: string
  userEmail: string
  name?: string
  siteId?: string
  organizationId?: string
  contextInfo: Record<string, unknown>
  chatLog: SerializedConversationMessage[]
  userActions: UserAction[]
  createdAt: UnixTime
  updatedAt: UnixTime
}

export interface AgentContextTags {
  organization?: string
  site?: string
  user?: string
}

export class GeckoAgentContext extends GeckoEntryBase<
  AgentContextData,
  AgentContextTags
> {
  private constructor(json: RolodexEntryJson) {
    const chatLog = Array.isArray(json.data.chatLog)
      ? (json.data.chatLog as SerializedConversationMessage[])
      : []

    const userActions = Array.isArray(json.data.userActions)
      ? (json.data.userActions as UserAction[])
      : []

    super(
      json,
      {
        sessionId: parseString(json.data.sessionId) ?? '',
        userId: parseString(json.data.userId) ?? '',
        userEmail: parseString(json.data.userEmail) ?? '',
        name: parseString(json.data.name),
        siteId: parseString(json.data.siteId),
        organizationId: parseString(json.data.organizationId),
        contextInfo: (json.data.contextInfo as Record<string, unknown>) ?? {},
        chatLog,
        userActions,
        createdAt: (parseNumber(json.data.createdAt) ?? Date.now()) as UnixTime,
        updatedAt: (parseNumber(json.data.updatedAt) ?? Date.now()) as UnixTime,
      },
      {
        organization: parseString(
          (json.tags as Record<string, unknown>).organization
        ),
        site: parseString((json.tags as Record<string, unknown>).site),
        user: parseString((json.tags as Record<string, unknown>).user),
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson): GeckoAgentContext {
    return new GeckoAgentContext(json)
  }
}

/**
 * Generates a unique entry ID and short session ID for a chat session.
 * Entry ID format: chat-session-MMDDYYYY-xxxxxx
 */
export function generateChatSessionId(): {
  entryId: string
  sessionId: string
} {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const year = now.getFullYear()
  const dateStr = `${month}${day}${year}`
  const shortUuid = crypto.randomUUID().slice(0, 6)
  return {
    entryId: `chat-session-${dateStr}-${shortUuid}`,
    sessionId: shortUuid,
  }
}
